import type { OpenPanelOptions } from '~/features/whatsapp/stores/chatPanelStore';
import type { ApplicantChatResolution } from '~/features/whatsapp/utils/resolveApplicantChats';

export const OPEN_LEAD_WHATSAPP_ACTIONS = {
    OPEN_PANEL: 'openPanel',
    START_THEN_LIST: 'startThenList',
    START_THEN_OPEN_CHAT: 'startThenOpenChat',
} as const;

export type OpenLeadWhatsAppAction =
    | { kind: typeof OPEN_LEAD_WHATSAPP_ACTIONS.OPEN_PANEL; options: OpenPanelOptions }
    | { kind: typeof OPEN_LEAD_WHATSAPP_ACTIONS.START_THEN_OPEN_CHAT; slot: 1 | 2 }
    | { kind: typeof OPEN_LEAD_WHATSAPP_ACTIONS.START_THEN_LIST; slots: Array<1 | 2> };

/**
 * Decision matrix for the "Chat" button on a lead. Callers must resolve `chats` (`getChats`)
 * before calling this — it assumes `canStartKn1`/`canStartKn2` reflect current data, not a cold
 * cache. Never land on the empty lead-scoped list when `startChat` is actually possible:
 *
 * - 1 KN, chat exists -> open it.
 * - 1 KN, startable, no chat -> `startChat` then jump in + template picker.
 * - 1 KN, not startable -> lead-scoped list (`ChatBlockedReason` shows the reason).
 * - 2 KN, both chats exist -> lead-scoped list (user picks KN1 or KN2).
 * - 2 KN, one chat exists and the missing slot is startable -> `startChat` the missing slot,
 *   then list (so the user picks between the two).
 * - 2 KN, one chat exists and the missing slot is blocked -> open the existing chat directly
 *   (not a one-row list; the KN2 chip surfaces the blocked reason).
 * - 2 KN, neither chat exists, both startable -> `startChat` both, then list.
 * - 2 KN, neither chat exists, exactly one startable -> `startChat` it, then jump straight in.
 * - 2 KN, neither chat exists, neither startable -> lead-scoped list with the blocked reason.
 */
export const resolveOpenLeadWhatsAppAction = ({
    canStartKn1,
    canStartKn2,
    loanRequestId,
    resolution,
}: {
    canStartKn1: boolean;
    canStartKn2: boolean;
    loanRequestId: string;
    resolution: ApplicantChatResolution;
}): OpenLeadWhatsAppAction => {
    const leadScopedList: OpenPanelOptions = { leadDetailsScopeLoanRequestId: loanRequestId };
    const openExisting = (chatId: string): OpenLeadWhatsAppAction => ({
        kind: OPEN_LEAD_WHATSAPP_ACTIONS.OPEN_PANEL,
        options: { chatId, leadDetailsScopeLoanRequestId: loanRequestId },
    });

    if (!resolution.hasCoApplicant) {
        if (resolution.kn1.chatId) return openExisting(resolution.kn1.chatId);
        if (canStartKn1) {
            return { kind: OPEN_LEAD_WHATSAPP_ACTIONS.START_THEN_OPEN_CHAT, slot: 1 };
        }
        return { kind: OPEN_LEAD_WHATSAPP_ACTIONS.OPEN_PANEL, options: leadScopedList };
    }

    const kn1ChatId = resolution.kn1.chatId;
    const kn2ChatId = resolution.kn2?.chatId ?? null;

    if (kn1ChatId && kn2ChatId) {
        return { kind: OPEN_LEAD_WHATSAPP_ACTIONS.OPEN_PANEL, options: leadScopedList };
    }

    if (kn1ChatId && !kn2ChatId) {
        if (canStartKn2) return { kind: OPEN_LEAD_WHATSAPP_ACTIONS.START_THEN_LIST, slots: [2] };
        return openExisting(kn1ChatId);
    }

    if (!kn1ChatId && kn2ChatId) {
        if (canStartKn1) return { kind: OPEN_LEAD_WHATSAPP_ACTIONS.START_THEN_LIST, slots: [1] };
        return openExisting(kn2ChatId);
    }

    // Neither chat exists yet.
    if (canStartKn1 && canStartKn2) {
        return { kind: OPEN_LEAD_WHATSAPP_ACTIONS.START_THEN_LIST, slots: [1, 2] };
    }
    if (canStartKn1) return { kind: OPEN_LEAD_WHATSAPP_ACTIONS.START_THEN_OPEN_CHAT, slot: 1 };
    if (canStartKn2) return { kind: OPEN_LEAD_WHATSAPP_ACTIONS.START_THEN_OPEN_CHAT, slot: 2 };
    return { kind: OPEN_LEAD_WHATSAPP_ACTIONS.OPEN_PANEL, options: leadScopedList };
};
